// Copyright (c) StrangeBee 2024

import { UseStoragePageReturn } from '@Hooks/useStoragePage';
import { UseStorageViewReturn } from '@Hooks/useStorageView';
import { Dropdown, Menu } from 'antd';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import isEqual from 'lodash.isequal';
import { useLocalMessage } from '@Helpers/locale';
import { classList } from '@Helpers/className';
import { useHistory, useParams } from 'react-router-dom';
import RouteBuilder from '@Services/RouteBuilder';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { ManageViewListDrawer } from './ManageViewListDrawer';
import { ManageViewCreateDrawer } from './ManageViewCreateDrawer';

import './ManageViewList.scss';
import { openMessage } from '@Helpers/notification';
import { useDrawer } from '@Hooks/useDrawer';

interface ManageViewListProps {
    viewManager: UseStorageViewReturn;
    pageManager: UseStoragePageReturn;
    beforeCreate?: (content: any) => any;
}

export const ManageViewList = ({ viewManager, pageManager, beforeCreate }: ManageViewListProps): ReactElement => {
    const local = useLocalMessage();

    const { viewId } = useParams<{ viewId: string }>();
    const { views, saveView, deleteView } = viewManager;
    const { page: storagePage, loadView, setCurrentView } = pageManager;
    const { visible, setVisible } = useDrawer({});
    const { visible: visibleCreate, setVisible: setVisibleCreate } = useDrawer({});

    const history = useHistory();
    const { currentView } = storagePage;
    const viewsName = useMemo(() => Object.keys(views), [views]);
    const [different, setDifferent] = useState(false);
    const [firstMount, setFirstMount] = useState(true);

    const [viewName, setViewName] = useState('');

    const saveViewHandler = (viewName: string, content?: any, oldViewName?: string): void => {
        saveView(content || storagePage, viewName, oldViewName);
    };

    const resetViewName = (): void => {
        setViewName('');
    };

    const onCreateHandler = (): void => {
        if (viewName.trim() === '') {
            return;
        }
        const contentProcessed = beforeCreate?.(storagePage);
        saveViewHandler(viewName, contentProcessed);
        if (!viewId) {
            setCurrentView(viewName);
        } else {
            history.push(RouteBuilder.compute('cases.views', { viewId: viewName }));
        }
        openMessage('success', local('view.message.create.success'));
        resetViewName();
    };

    const onSaveHandler = (): void => {
        if (currentView === 'default') {
            return;
        }
        const contentProcessed = beforeCreate?.(storagePage);
        saveViewHandler(currentView, contentProcessed);
        openMessage('success', local('view.message.create.success'));
    };

    useEffect(() => {
        const { currentView, ...content } = storagePage;

        // eslint-disable-next-line
        const { currentView: __, ...view } = views[currentView];

        setDifferent(!isEqual(content, view));
    }, [storagePage, views]);

    useEffect(() => {
        if (!firstMount && !viewId) {
            loadView(currentView, views[currentView]);
        }
    }, [currentView]); //eslint-disable-line

    useEffect(() => {
        setFirstMount(false);
    }, []);

    const menu = (
        <Menu className="manage-view-list__dropdown__list">
            {viewsName.map((value) => (
                <Menu.Item
                    key={value}
                    className={classList('manage-view-list__dropdown__field', { active: currentView === value })}
                    onClick={(): void => {
                        if (value === currentView) {
                            loadView(currentView, views[currentView]);
                        } else {
                            if (viewId) {
                                history.push(RouteBuilder.compute('cases.views', { viewId: value }));
                            } else {
                                setCurrentView(value);
                            }
                        }
                    }}
                >
                    {value}
                </Menu.Item>
            ))}
            <Menu.Divider />
            {different && currentView !== 'default' && (
                <Menu.Item
                    key="__manage-view-drawer-save"
                    className={classList('manage-view-list__dropdown__field')}
                    onClick={(): void => {
                        onSaveHandler();
                    }}
                >
                    {local('view.btn.update')}
                </Menu.Item>
            )}
            {different && (
                <Menu.Item key="__manage-view-drawer-create">
                    <span className="manage-view-list__btn" onClick={() => setVisibleCreate(true)}>
                        {local('view.btn.open-create')}
                    </span>
                </Menu.Item>
            )}
            <Menu.Item key="__manage-view-drawer-list">
                <span className="manage-view-list__btn" onClick={() => setVisible(true)}>
                    {local('view.btn.open-list')}
                </span>
            </Menu.Item>
        </Menu>
    );

    return (
        <div className="manage-view-list">
            <Dropdown overlay={menu} trigger={['click']}>
                <THButton type="default">
                    <span>{`${currentView}${different ? '*' : ''}`}</span>
                </THButton>
            </Dropdown>
            {visible && (
                <ManageViewListDrawer
                    views={views}
                    visible={visible}
                    onVisibleChange={setVisible}
                    onDelete={(value) => {
                        deleteView(value);
                        if (value === currentView) {
                            setCurrentView('default');
                        }
                        openMessage('success', local('view.message.delete.success'));
                    }}
                />
            )}
            {visibleCreate && (
                <ManageViewCreateDrawer
                    views={views}
                    visible={visibleCreate}
                    onVisibleChange={setVisibleCreate}
                    onChange={(value: string): void => {
                        if (value.length > 1) {
                            setViewName(value);
                        }
                    }}
                    onCreate={() => onCreateHandler()}
                />
            )}
        </div>
    );
};
